import { useQuery } from '@tanstack/react-query'
import { api } from '../../api/client'
import { keys } from '../../api/keys'
import type { AsyncState } from '../../platform/async/Async'
import { fromQuery } from '../../platform/async/fromQuery'
import { monthBounds, toQuery, type TxnFilters, type TxnQuery } from './filters'
import { toTxn, type Txn } from './transactionWire'

export type TxnPage = { rows: Txn[]; total: number; page: number; hasMore: boolean }

export class TransactionsError extends Error {
  readonly status: number | undefined
  constructor(cause: unknown, status?: number) {
    super('transactions-unavailable', { cause })
    this.name = 'TransactionsError'
    this.status = status
  }
}

/**
 * `signal` is TanStack's, handed straight to the request. A superseded query
 * (the user kept typing, or changed a filter) is cancelled only if its signal
 * was consumed, so passing it is what makes the abort real rather than a
 * response that is merely ignored on arrival (Review Focus 4).
 */
export async function fetchPage(query: TxnQuery, signal: AbortSignal): Promise<TxnPage> {
  const { data, error, response } = await api.GET('/api/v1/transactions', { params: { query }, signal })
  if (error !== undefined || data === undefined) throw new TransactionsError(error, response.status)
  return {
    rows: data.transactions.map(toTxn),
    total: data.total,
    page: data.page,
    hasMore: data.has_more,
  }
}

export function sameExceptPage(previous: unknown, next: TxnQuery): boolean {
  if (typeof previous !== 'object' || previous === null) return false
  const strip = (q: object) => JSON.stringify({ ...q, page: undefined })
  return strip(previous) === strip(next)
}

/**
 * `live` (Q7): no `asOf`, so the state is never stale. The hook never returns
 * `empty` either — whether zero rows means "nothing yet" or "nothing matches"
 * depends on the filters, which is the screen's call, so a zero-total page is a
 * success.
 *
 * The last page stays on screen while the next one loads **only when the page
 * number alone changed**, so paging does not flash a skeleton or unmount the grid
 * out from under a focused cell. A changed month, search or filter shows loading:
 * the old rows would sit under the new month and could be selected under it.
 * `fetching` is how the screen says "Updating…" in that gap.
 */
export function useTransactions(f: TxnFilters): AsyncState<TxnPage> & { fetching: boolean } {
  return useTransactionPage(toQuery(f))
}

/**
 * The same page query for a caller that already holds a `TxnQuery` (the budget detail's
 * envelope scope): the request is exactly `query`, under `keys.transactions.list(query)` so
 * any transaction write invalidates it, with the same page-only placeholder rule and the same
 * contract (never `empty`; `fetching` for "Updating…").
 */
export function useTransactionPage(query: TxnQuery): AsyncState<TxnPage> & { fetching: boolean } {
  const result = useQuery({
    queryKey: keys.transactions.list(query),
    queryFn: ({ signal }) => fetchPage(query, signal),
    placeholderData: (previous, previousQuery) =>
      previousQuery !== undefined && sameExceptPage(previousQuery.queryKey[2], query) ? previous : undefined,
  })
  const state = fromQuery(result, {
    message: () => 'Transactions could not be loaded.',
    recovery: { label: 'Try again', onAct: () => void result.refetch() },
  })
  return { ...state, fetching: result.isFetching }
}

/**
 * The "N to review" count: the `total` of a one-row page of pending rows. Not
 * date-scoped on the server's review summary, so this is the month's own count
 * and agrees with what the Needs review chip will show.
 */
export function useReviewCount(f: Pick<TxnFilters, 'month'>): number | undefined {
  const query = useQuery({
    queryKey: keys.transactions.reviewCount(f.month),
    queryFn: async ({ signal }) => {
      const q: TxnQuery = { review_status: 'pending', page: 1, page_size: 1 }
      if (f.month !== null) {
        const { start, end } = monthBounds(f.month)
        q.start_date = start
        q.end_date = end
      }
      return (await fetchPage(q, signal)).total
    },
  })
  return query.data
}

/**
 * The last row's date on the page before this one, for "(continued)" when a day
 * runs across the boundary. Reads the same cache entry paging backwards would,
 * so it costs a request only the first time.
 */
export function usePreviousPageTail(f: TxnFilters): string | undefined {
  const previous = { ...f, page: Math.max(1, f.page - 1) }
  const query = useQuery({
    queryKey: keys.transactions.list(toQuery(previous)),
    queryFn: ({ signal }) => fetchPage(toQuery(previous), signal),
    enabled: f.page > 1,
    select: (p: TxnPage) => p.rows.at(-1)?.date,
  })
  return f.page > 1 ? query.data : undefined
}

async function fetchTags(signal: AbortSignal): Promise<string[]> {
  const { data, error, response } = await api.GET('/api/v1/transactions/tags', { signal })
  if (error !== undefined || data === undefined) throw new TransactionsError(error, response.status)
  return data.tags
}

/** The one key and fetcher for the tag list; `fetchQuery({ ...tagListQuery(), staleTime: 0 })` reads it fresh. */
export function tagListQuery() {
  return { queryKey: keys.transactions.tags(), queryFn: ({ signal }: { signal: AbortSignal }) => fetchTags(signal) }
}

/**
 * Every distinct tag on the caller's transactions, for the filter's tag picker and the rule builder's
 * add/remove-tag suggestions.
 * `live` and four-state like the list; an empty list is a success (a household
 * that never tagged anything), and the picker simply offers only "Any tag".
 */
export function useTransactionTags(): AsyncState<string[]> {
  const query = useQuery(tagListQuery())
  return fromQuery(query, {
    message: () => 'Tags could not be loaded.',
    recovery: { label: 'Try again', onAct: () => void query.refetch() },
  })
}

/**
 * The same tag list for `/tags`, where an empty list is a screen of its own rather than a picker
 * with one option. Same key and fetcher as `useTransactionTags`, so the two share one request.
 */
export function useTagList(): AsyncState<string[]> {
  const query = useQuery(tagListQuery())
  return fromQuery(query, {
    isEmpty: (tags) => tags.length === 0,
    message: () => 'Tags could not be loaded.',
    recovery: { label: 'Try again', onAct: () => void query.refetch() },
  })
}

/**
 * One tag's usage: the `total` of `GET /transactions?tag=<name>&page_size=1`. `page_size: 1` because
 * only the total is read; no row is needed. There is no count endpoint, so this is the count source.
 * It can sit a little BELOW the `updated` a later rename or delete reports: the list excludes
 * `is_removed` rows, which the rename/delete scope does not. Copy after a mutation uses `updated`.
 */
export function useTagCount(name: string, enabled: boolean): { status: 'loading' | 'error' | 'success'; count?: number } {
  const query = useQuery({
    queryKey: keys.transactions.tagCount(name),
    enabled,
    queryFn: async ({ signal }) => {
      const { data, error, response } = await api.GET('/api/v1/transactions', {
        params: { query: { tag: name, page: 1, page_size: 1 } },
        signal,
      })
      if (error !== undefined || data === undefined) throw new TransactionsError(error, response.status)
      return data.total
    },
  })
  if (query.data !== undefined) return { status: 'success', count: query.data }
  return { status: query.isError ? 'error' : 'loading' }
}
